import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Users, LineChart, FileText, Trophy, Repeat, Settings,
  MessageSquareText, Sparkles, ArrowRight, Check, Play,
} from "lucide-react";

/**
 * MyFantabusiness — Landing Page
 * Style: premium sports management platform. Blu notte / antracite bg,
 * bianco testi, verde acceso CTA, oro solo per dettagli premium.
 */
export default function Landing() {
  const { user, setUser } = useAuth();
  const primaryCTAHref = user ? "/dashboard" : "/register";
  const nav = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/pricing/public").then((r) => setPricing(r.data)).catch(() => {});
    // Iter 71.8 — track visit landing (no cookies, best-effort)
    api.post("/analytics/track", { event: "visit", path: "/", referrer: document.referrer || "" }).catch(() => {});
  }, []);

  async function startDemo() {
    setDemoLoading(true);
    api.post("/analytics/track", { event: "demo_open", path: "/" }).catch(() => {});
    try {
      const { data } = await api.post("/demo/start");
      setUser(data.user);
      toast.success("Benvenuto nella demo! Naviga liberamente.");
      nav(data.redirect, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDemoLoading(false); }
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-white antialiased">
      {/* Background subtle gradient — no arcade */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1120] via-[#0d1428] to-[#0b1120]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-emerald-500/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-amber-400/[0.04] rounded-full blur-[100px]" />
      </div>

      {/* HEADER FISSO */}
      <header className="sticky top-0 z-40 border-b border-white/[0.06] backdrop-blur-xl bg-[#0b1120]/80">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/brand/logo-256.png"
              alt="MyFantabusiness" className="w-11 h-11 object-contain drop-shadow-[0_4px_12px_rgba(16,185,129,0.35)]"/>
            <div className="font-black tracking-tight text-[17px] leading-none">
              My<span className="text-emerald-400">Fantabusiness</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-300">
            <a href="#come-funziona" className="hover:text-white transition-colors" data-testid="nav-come-funziona">Come funziona</a>
            <a href="#funzionalita" className="hover:text-white transition-colors" data-testid="nav-funzionalita">Funzionalità</a>
            <a href="#prezzo" className="hover:text-white transition-colors" data-testid="nav-prezzo">Prezzo</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="nav-accedi"
              className="inline-flex items-center px-3 sm:px-4 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-300 text-slate-200 font-bold text-sm transition-colors">
              Accedi
            </Link>
            <Link to={primaryCTAHref} data-testid="nav-cta"
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-bold text-sm transition-colors shadow-[0_4px_16px_rgba(16,185,129,0.35)]">
              Crea la tua lega
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/25 text-amber-300 text-[11px] font-mono uppercase tracking-widest mb-6">
              <Sparkles size={11} /> Nuova generazione fantacalcio
            </div>
            <h1 className="text-[clamp(2.5rem,5vw,4.25rem)] font-black leading-[1.05] tracking-tight text-white">
              Il fantacalcio finisce a maggio.
              <span className="block text-emerald-400 mt-2">La tua società continua.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
              Rose permanenti, mercato, contratti, stipendi, bilanci e strategia.
              Crea una lega privata e gestisci una vera società calcistica.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={primaryCTAHref} data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-base transition-all shadow-[0_8px_24px_rgba(16,185,129,0.45)] hover:shadow-[0_12px_32px_rgba(16,185,129,0.55)] hover:-translate-y-0.5">
                Crea la tua lega – 69 €
                <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <button
                type="button"
                onClick={startDemo}
                disabled={demoLoading}
                data-testid="hero-cta-demo"
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] border border-amber-400/50 text-amber-300 hover:text-amber-200 font-bold text-sm transition-colors disabled:opacity-60"
              >
                <Play size={14} strokeWidth={2.5} className="fill-amber-400"/> {demoLoading ? "Preparo la demo…" : "Prova la demo · Gratis"}
              </button>
              <a href="#come-funziona" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl text-slate-300 hover:text-white border border-white/10 hover:border-white/25 font-semibold text-sm transition-colors">
                Scopri come funziona
              </a>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Offerta lancio · Prezzo per l&apos;intera lega, valido per una stagione ·{" "}
              <span className="text-amber-400/90">39 € per chi crea la sua prima lega da ottobre a marzo</span>
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              🎮 <span className="text-amber-400/80">Demo</span>: entra subito con una lega precaricata, prova tutto senza registrarti.
            </p>
          </div>

          {/* Dashboard mockup (finto-real) */}
          <HeroMockup />
        </div>
      </section>

      {/* BLOCCO DIFFERENZIANTE */}
      <section className="border-y border-white/[0.06] bg-white/[0.015]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-black tracking-tight leading-[1.15] max-w-3xl">
            Non gestisci soltanto una formazione. <span className="text-emerald-400">Costruisci un club.</span>
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ConceptCard title="Rosa che continua negli anni" desc="I giocatori restano tuoi. Ogni stagione parti da dove sei arrivato." />
            <ConceptCard title="Contratti e stipendi" desc="Ogni cartellino ha un costo che pesa sui bilanci futuri." />
            <ConceptCard title="Patrimonio societario" desc="Budget, plusvalenze, tesoro. Ogni scelta cambia il conto." />
            <ConceptCard title="Mercato tra partecipanti" desc="Trattative, aste, scambi. Un vero mercato manageriale." />
          </div>
          <p className="mt-12 text-center text-lg text-slate-400 font-medium">
            Ogni scelta cambia il futuro della tua società.
          </p>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="max-w-6xl mx-auto px-5 sm:px-8 py-24 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">Come funziona</div>
          <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-black tracking-tight leading-[1.15]">
            Tre passaggi. Poi lasci parlare la strategia.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <StepCard n="1" title="Crea la lega" desc="Scegli nome, partecipanti e regolamento." />
          <StepCard n="2" title="Invita gli amici" desc="Condividi il link o il codice privato." />
          <StepCard n="3" title="Inizia la stagione" desc="Ogni partecipante gestisce la propria società." />
        </div>
      </section>

      {/* FUNZIONALITÀ */}
      <section id="funzionalita" className="border-y border-white/[0.06] bg-white/[0.015] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">Funzionalità</div>
            <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-black tracking-tight leading-[1.15]">
              Tutto quello che serve per gestire un club.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureCard Icon={Users} title="Società permanenti" desc="Ogni club esiste anno dopo anno con storico e palmares." />
            <FeatureCard Icon={Repeat} title="Mercato manageriale" desc="Aste, trattative, scambi tra i partecipanti in tempo reale." />
            <FeatureCard Icon={FileText} title="Contratti e stipendi" desc="Ogni giocatore ha un costo che pesa sui prossimi bilanci." />
            <FeatureCard Icon={LineChart} title="Bilanci e patrimonio" desc="Budget, ricavi, plusvalenze e patrimonio in un colpo d'occhio." />
            <FeatureCard Icon={Settings} title="Regolamento personalizzabile" desc="Adatti le regole al vostro campionato senza compromessi." />
            <FeatureCard Icon={Trophy} title="Storico delle stagioni" desc="Ogni trofeo, ogni trasferimento, ogni bilancio resta." />
          </div>
        </div>
      </section>

      {/* AI MANAGER */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">AI Manager</div>
            <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-black tracking-tight leading-[1.1]">
              Un consulente AI per <span className="text-emerald-400">ogni società.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">
              Analizza la rosa, valuta uno scambio, controlla il bilancio e prepara la strategia di mercato.
            </p>
            <div className="mt-8 space-y-2.5">
              <PromptChip text="Posso permettermi questo acquisto?" />
              <PromptChip text="Chi dovrei vendere?" />
              <PromptChip text="Analizza questa proposta di scambio." />
              <PromptChip text="Quale reparto devo rinforzare?" />
            </div>
          </div>
          <div className="rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-7 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl -translate-y-10 translate-x-10" />
            <div className="relative">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">Borsellino AI</div>
              <div className="text-4xl font-black text-white">
                3 <span className="text-lg text-slate-400 font-bold">chiamate gratuite</span>
              </div>
              <div className="text-slate-400 text-sm mt-1">per ogni utente</div>
              <div className="mt-5 py-4 border-y border-white/10">
                <div className="text-3xl font-black text-white">
                  0,50 <span className="text-base text-slate-500 font-normal">€ / chiamata</span>
                </div>
                <div className="text-slate-500 text-xs mt-1">dopo le prime 3 chiamate</div>
              </div>
              <div className="mt-5 space-y-2">
                <WalletFeature text="Borsellino personale per ogni utente" />
                <WalletFeature text="Ricarica manuale, quando serve" />
                <WalletFeature text="Saldo sempre visibile" />
                <WalletFeature text="Conferma prima di ogni chiamata a pagamento" />
                <WalletFeature text="Nessun rinnovo automatico" highlight />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PREZZO */}
      <section id="prezzo" className="border-y border-white/[0.06] bg-white/[0.015] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">Prezzo</div>
            <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-black tracking-tight leading-[1.15]">
              Un prezzo unico. Per tutta la lega. Per tutta la stagione.
            </h2>
          </div>
          <div className="max-w-md mx-auto">
            <div className="relative rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/10 p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
              <div className="absolute -top-3 left-8 px-2.5 py-0.5 rounded-md bg-amber-400 text-[#0b1120] text-[10px] font-black uppercase tracking-widest">
                Offerta lancio
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-6xl font-black text-white">69</span>
                <span className="text-2xl text-slate-400 font-bold">€</span>
                <span className="text-slate-400 text-sm">a stagione</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <span className="text-[10px] uppercase tracking-widest font-black text-amber-300">Sconto prima lega</span>
                <span className="text-sm font-bold text-white">
                  <span className="line-through text-slate-500">69€</span> → <span className="text-amber-300">39€</span>
                </span>
              </div>
              {pricing?.off_season_active && pricing?.off_season_days_left > 0 && (
                <div data-testid="offseason-countdown" className="mt-2 flex items-center gap-2 text-[11px] text-amber-400/90 font-mono">
                  <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"/>
                  Sconto attivo ancora per <span className="font-bold text-amber-300">{pricing.off_season_days_left} giorni</span> (fino al 31 marzo)
                </div>
              )}
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                Chi crea la sua prima lega tra <span className="text-white font-bold">ottobre e marzo</span> (fuori stagione) paga 39 €.
                Poi 69 € fissi, stesse regole.
              </p>
              <div className="mt-6 space-y-2.5">
                <PriceRow text="Intera lega" />
                <PriceRow text="Tutti i partecipanti" />
                <PriceRow text="Gestione società completa" />
                <PriceRow text="Mercato manageriale" />
                <PriceRow text="Contratti e stipendi" />
                <PriceRow text="Bilanci e patrimonio" />
                <PriceRow text="Storico permanente" />
                <PriceRow text="3 chiamate AI gratuite per ogni utente" highlight />
              </div>
              <Link to={primaryCTAHref} data-testid="prezzo-cta"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-base transition-all shadow-[0_8px_20px_rgba(16,185,129,0.4)]">
                Crea la lega <ArrowRight size={17}/>
              </Link>
              <p className="mt-4 text-center text-xs text-slate-400 leading-relaxed">
                In una lega da 10 partecipanti sono <span className="text-white font-bold">6,90 € a persona</span> (o <span className="text-amber-300 font-bold">3,90 €</span> se prendi lo sconto prima lega).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] font-black tracking-tight leading-[1.1]">
          La prossima stagione <span className="text-emerald-400">non ripartire da zero.</span>
        </h2>
        <p className="mt-6 text-lg text-slate-300 max-w-xl mx-auto leading-relaxed">
          Costruisci una lega in cui vittorie, errori, investimenti e rivalità restano nel tempo.
        </p>
        <div className="mt-9 flex flex-col items-center gap-3">
          <Link to={primaryCTAHref} data-testid="final-cta"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-lg transition-all shadow-[0_10px_28px_rgba(16,185,129,0.45)] hover:-translate-y-0.5">
            Crea la tua lega – 69 € <ArrowRight size={20}/>
          </Link>
          <Link to="/login" data-testid="join-with-code"
            className="text-sm text-slate-400 hover:text-emerald-300 underline decoration-slate-700 hover:decoration-emerald-400 underline-offset-4 transition-colors">
            Hai già un codice lega? Entra
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-128.png"
              alt="MyFantabusiness" className="w-6 h-6 object-contain"/>
            <span className="text-slate-400">MyFantabusiness</span>
            <span className="text-slate-700">·</span>
            <span>© {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-5">
            <a href="#prezzo" className="hover:text-slate-300 transition-colors">Prezzo</a>
            <a href="#funzionalita" className="hover:text-slate-300 transition-colors">Funzionalità</a>
            <Link to="/login" className="hover:text-slate-300 transition-colors">Accedi</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function ConceptCard({ title, desc }) {
  return (
    <div className="p-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-emerald-500/30 hover:bg-white/[0.03] transition-all">
      <div className="text-white font-black text-[15px] leading-tight">{title}</div>
      <div className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function StepCard({ n, title, desc }) {
  return (
    <div className="p-6 rounded-3xl border border-white/[0.08] bg-white/[0.025] hover:border-emerald-500/40 hover:bg-white/[0.04] transition-all">
      <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-3">Step {n}</div>
      <div className="text-white font-black text-xl leading-tight">{title}</div>
      <div className="mt-2 text-slate-400 text-sm leading-relaxed">{desc}</div>
    </div>
  );
}

function FeatureCard({ Icon, title, desc }) {
  return (
    <div className="p-6 rounded-3xl border border-white/[0.08] bg-white/[0.025] hover:border-emerald-500/40 hover:bg-white/[0.045] transition-all">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/25 to-emerald-500/10 border border-emerald-500/40 flex items-center justify-center mb-5 shadow-[0_4px_16px_rgba(16,185,129,0.15)]">
        <Icon size={26} className="text-emerald-300 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" strokeWidth={2.25}/>
      </div>
      <div className="text-white font-black text-[15px] leading-tight">{title}</div>
      <div className="mt-1.5 text-sm text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function PromptChip({ text }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.08] text-slate-200 text-[14px] w-full sm:w-fit sm:max-w-none">
      <MessageSquareText size={14} className="text-emerald-400 shrink-0" strokeWidth={2}/>
      <span>{text}</span>
    </div>
  );
}

function WalletFeature({ text, highlight }) {
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${highlight ? "bg-amber-400" : "bg-emerald-500/20 border border-emerald-500/30"}`}>
        <Check size={10} className={highlight ? "text-[#0b1120]" : "text-emerald-400"} strokeWidth={3}/>
      </div>
      <span className={highlight ? "text-amber-300 font-semibold" : "text-slate-300"}>{text}</span>
    </div>
  );
}

function PriceRow({ text, highlight }) {
  return (
    <div className="flex items-center gap-3 text-sm py-1">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${highlight ? "bg-amber-400" : "bg-emerald-500"}`}>
        <Check size={12} className="text-[#0b1120]" strokeWidth={3.5}/>
      </div>
      <span className={highlight ? "text-amber-200 font-semibold" : "text-slate-200"}>{text}</span>
    </div>
  );
}

/* -------- Hero Dashboard Mockup (realistic, no stock) -------- */
function HeroMockup() {
  return (
    <div className="relative">
      <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#111a30] to-[#0d1428] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
        {/* Mock header */}
        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2 text-[11px] text-slate-500 font-mono">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
          </div>
          <div className="ml-3 text-slate-400 tracking-wider uppercase">Fc Castelverde · 2026/27</div>
          <div className="ml-auto text-emerald-400/80">● Live</div>
        </div>
        {/* Mock content */}
        <div className="p-5 grid grid-cols-3 gap-3">
          <MockKpi label="Budget" value="1.285" unit="cr" tint="emerald" />
          <MockKpi label="Valore rosa" value="2.733" unit="cr" tint="amber" />
          <MockKpi label="Ranking" value="20.5k" unit="pt" tint="slate" />
        </div>
        <div className="px-5 pb-4">
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Andamento costo rosa</div>
              <div className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                +17.6% vs 25/26
              </div>
            </div>
            <MockChart />
            <div className="mt-1 flex justify-between text-[9px] font-mono text-slate-600">
              <span>23/24</span><span>24/25</span><span>25/26</span><span className="text-emerald-400">26/27</span>
            </div>
          </div>
        </div>
        <div className="px-5 pb-5">
          <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-widest mb-2">
            <Trophy size={11} className="text-amber-400"/> Palmares
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["🏆 Campionato ×2", "🥇 Coppa di Lega ×2", "👑 Supercoppa ×1"].map((p, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-amber-400/10 border border-amber-400/25 text-amber-300 font-mono">{p}</span>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute -bottom-3 -right-3 px-3 py-1.5 rounded-xl bg-emerald-500 text-[#0b1120] font-black text-[11px] uppercase tracking-widest shadow-xl">
        La tua società
      </div>
    </div>
  );
}

function MockKpi({ label, value, unit, tint }) {
  const tints = {
    emerald: "text-emerald-400",
    amber: "text-amber-300",
    slate: "text-slate-200",
  };
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] p-3">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-black text-xl tabular-nums ${tints[tint]}`}>
        {value}<span className="text-[10px] text-slate-500 font-normal ml-1">{unit}</span>
      </div>
    </div>
  );
}

function MockChart() {
  // Path drawn to mimic 572 → 1271 → 2159 → 2540 (rough scaling)
  return (
    <svg viewBox="0 0 200 60" className="w-full h-14" preserveAspectRatio="none">
      <defs>
        <linearGradient id="mockGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M0 52 L67 40 L133 20 L200 12 L200 60 L0 60 Z" fill="url(#mockGrad)" />
      <path d="M0 52 L67 40 L133 20 L200 12" stroke="#10b981" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      {[[0,52],[67,40],[133,20],[200,12]].map(([x,y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill="#0b1120" stroke="#10b981" strokeWidth="1.6" />
      ))}
    </svg>
  );
}
